import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CreditCardIcon,
  PlusIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend,
} from 'recharts';
import { format } from 'date-fns';
import { StatCard } from '../components/ui';
import Modal, { ModalFooter } from '../components/ui/Modal';
import Button from '../components/ui/Button';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

// ============ API HELPERS ============

const financeApi = {
  // Summary
  getSummary: () => fetch(`${API_URL}/finance/summary`, { headers: authHeaders() }).then(r => r.json()),
  getCashFlow: (dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    return fetch(`${API_URL}/finance/cash-flow?${params}`, { headers: authHeaders() }).then(r => r.json());
  },
  getExpensesByCategory: (farmId?: string) => {
    const params = new URLSearchParams();
    if (farmId) params.set('farmId', farmId);
    return fetch(`${API_URL}/finance/expenses/by-category?${params}`, { headers: authHeaders() }).then(r => r.json());
  },
  getProfitLoss: (farmId?: string, dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams();
    if (farmId) params.set('farmId', farmId);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    return fetch(`${API_URL}/finance/profit-loss?${params}`, { headers: authHeaders() }).then(r => r.json());
  },
  getROI: (farmId?: string, season?: string) => {
    const params = new URLSearchParams();
    if (farmId) params.set('farmId', farmId);
    if (season) params.set('season', season);
    return fetch(`${API_URL}/finance/roi?${params}`, { headers: authHeaders() }).then(r => r.json());
  },
  getBreakEven: (farmId: string, season: string) =>
    fetch(`${API_URL}/finance/break-even?farmId=${farmId}&season=${season}`, { headers: authHeaders() }).then(r => r.json()),

  // Budgets
  getBudgets: (farmId?: string, season?: string, status?: string) => {
    const params = new URLSearchParams();
    if (farmId) params.set('farmId', farmId);
    if (season) params.set('season', season);
    if (status) params.set('status', status);
    return fetch(`${API_URL}/finance/budgets?${params}`, { headers: authHeaders() }).then(r => r.json());
  },
  getBudget: (id: string) => fetch(`${API_URL}/finance/budgets/${id}`, { headers: authHeaders() }).then(r => r.json()),
  createBudget: (data: Record<string, unknown>) => fetch(`${API_URL}/finance/budgets`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(r => r.json()),
  closeBudget: (id: string) => fetch(`${API_URL}/finance/budgets/${id}/close`, { method: 'PATCH', headers: authHeaders() }).then(r => r.json()),

  // Expenses
  getExpenses: (filters?: Record<string, string>) => {
    const params = new URLSearchParams(filters);
    return fetch(`${API_URL}/finance/expenses?${params}`, { headers: authHeaders() }).then(r => r.json());
  },
  addExpense: (data: Record<string, unknown>) => fetch(`${API_URL}/finance/expenses`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(r => r.json()),
  deleteExpense: (id: string) => fetch(`${API_URL}/finance/expenses/${id}`, { method: 'DELETE', headers: authHeaders() }).then(r => r.json()),

  // Income
  getIncome: (filters?: Record<string, string>) => {
    const params = new URLSearchParams(filters);
    return fetch(`${API_URL}/finance/income?${params}`, { headers: authHeaders() }).then(r => r.json());
  },
  addIncome: (data: Record<string, unknown>) => fetch(`${API_URL}/finance/income`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(r => r.json()),
  deleteIncome: (id: string) => fetch(`${API_URL}/finance/income/${id}`, { method: 'DELETE', headers: authHeaders() }).then(r => r.json()),

  // Loans
  getLoans: (status?: string) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    return fetch(`${API_URL}/finance/loans?${params}`, { headers: authHeaders() }).then(r => r.json());
  },
  addLoan: (data: Record<string, unknown>) => fetch(`${API_URL}/finance/loans`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) }).then(r => r.json()),
  recordPayment: (id: string, amount: number) => fetch(`${API_URL}/finance/loans/${id}/payment`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ amount }) }).then(r => r.json()),
  getLoanSummary: () => fetch(`${API_URL}/finance/loans/summary`, { headers: authHeaders() }).then(r => r.json()),

  // Farms list for selects
  getFarms: () => fetch(`${API_URL}/farms`, { headers: authHeaders() }).then(r => r.json()),
};

// ============ FORMATTERS ============

function formatKES(amount: number): string {
  return `KES ${amount.toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// ============ CONSTANTS ============

const EXPENSE_COLORS: Record<string, string> = {
  seeds: '#22c55e', fertilizer: '#f59e0b', pesticide: '#ef4444',
  labor: '#3b82f6', equipment: '#8b5cf6', transport: '#06b6d4',
  irrigation: '#0ea5e9', land_rent: '#a855f7', insurance: '#64748b', other: '#94a3b8',
};

const EXPENSE_LABELS: Record<string, string> = {
  seeds: 'Seeds', fertilizer: 'Fertilizer', pesticide: 'Pesticide',
  labor: 'Labor', equipment: 'Equipment', transport: 'Transport',
  irrigation: 'Irrigation', land_rent: 'Land Rent', insurance: 'Insurance', other: 'Other',
};

const INCOME_LABELS: Record<string, string> = {
  crop_sale: 'Crop Sale', livestock: 'Livestock', subsidy: 'Subsidy',
  cooperative_dividend: 'Co-op Dividend', other: 'Other',
};

const LOAN_TYPE_LABELS: Record<string, string> = {
  input_financing: 'Input Financing', equipment: 'Equipment', land: 'Land',
  working_capital: 'Working Capital', other: 'Other',
};

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: ChartBarIcon },
  { id: 'expenses', label: 'Expenses', icon: ArrowTrendingDownIcon },
  { id: 'income', label: 'Income', icon: ArrowTrendingUpIcon },
  { id: 'budgets', label: 'Budgets', icon: ClipboardDocumentListIcon },
  { id: 'loans', label: 'Loans', icon: CreditCardIcon },
  { id: 'reports', label: 'Reports', icon: CalculatorIcon },
] as const;

type TabId = typeof TABS[number]['id'];

// ============ MAIN COMPONENT ============

export default function Finance() {
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Financial Management</h1>
        <p className="text-gray-500">Track expenses, income, budgets, and loans</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1 overflow-x-auto pb-px">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors
                ${activeTab === tab.id
                  ? 'border-kenagri-600 text-kenagri-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && <DashboardTab />}
      {activeTab === 'expenses' && <ExpensesTab />}
      {activeTab === 'income' && <IncomeTab />}
      {activeTab === 'budgets' && <BudgetsTab />}
      {activeTab === 'loans' && <LoansTab />}
      {activeTab === 'reports' && <ReportsTab />}
    </div>
  );
}

// ============ DASHBOARD TAB ============

function DashboardTab() {
  const { data: summary, isLoading } = useQuery({
    queryKey: ['finance-summary'],
    queryFn: financeApi.getSummary,
  });

  const { data: cashFlowData } = useQuery({
    queryKey: ['finance-cashflow'],
    queryFn: () => financeApi.getCashFlow(),
  });

  const { data: expenseCategories } = useQuery({
    queryKey: ['finance-expense-categories'],
    queryFn: () => financeApi.getExpensesByCategory(),
  });

  const cashFlow = cashFlowData?.cashFlow || [];
  const pieData = (Array.isArray(expenseCategories) ? expenseCategories : []).map((c: { category: string; total: string }) => ({
    name: EXPENSE_LABELS[c.category] || c.category,
    value: parseFloat(c.total),
    color: EXPENSE_COLORS[c.category] || '#94a3b8',
  }));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kenagri-600" />
      </div>
    );
  }

  const netProfit = summary?.netProfit || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue"
          value={formatKES(summary?.totalRevenue || 0)}
          icon={ArrowTrendingUpIcon}
          color="green"
        />
        <StatCard
          title="Total Expenses"
          value={formatKES(summary?.totalExpenses || 0)}
          icon={ArrowTrendingDownIcon}
          color="amber"
        />
        <StatCard
          title="Net Profit"
          value={formatKES(netProfit)}
          icon={BanknotesIcon}
          color={netProfit >= 0 ? 'green' : 'rose'}
        />
        <StatCard
          title="Active Loans"
          value={formatKES(summary?.pendingLoans || 0)}
          icon={CreditCardIcon}
          color="blue"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cash Flow Chart */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Cash Flow (Last 12 Months)</h3>
          <div className="h-72">
            {cashFlow.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cashFlow}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value: number) => formatKES(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="income" name="Income" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="expenses" name="Expenses" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="net" name="Net" stroke="#3b82f6" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                No cash flow data yet. Record expenses and income to see trends.
              </div>
            )}
          </div>
        </div>

        {/* Expense Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Expense Breakdown</h3>
          <div className="h-72">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ name, percent }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry: { color: string }, index: number) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatKES(value)} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                No expenses recorded yet.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Budget Utilization */}
      {summary?.activeBudgets && summary.activeBudgets.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Budget Utilization</h3>
          <div className="space-y-4">
            {summary.activeBudgets.map((b: {
              id: string; season: string; farmName: string;
              plannedExpenses: number; actualExpenses: number;
              plannedRevenue: number; actualRevenue: number;
            }) => {
              const expPct = b.plannedExpenses > 0 ? Math.min((b.actualExpenses / b.plannedExpenses) * 100, 100) : 0;
              const revPct = b.plannedRevenue > 0 ? Math.min((b.actualRevenue / b.plannedRevenue) * 100, 100) : 0;
              return (
                <div key={b.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <p className="font-medium text-gray-900">{b.farmName || 'Unknown Farm'} - {b.season}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">Expenses</span>
                        <span className="font-medium">{formatKES(b.actualExpenses)} / {formatKES(b.plannedExpenses)}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${expPct > 90 ? 'bg-red-500' : expPct > 70 ? 'bg-amber-500' : 'bg-kenagri-500'}`}
                          style={{ width: `${expPct}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">Revenue</span>
                        <span className="font-medium">{formatKES(b.actualRevenue)} / {formatKES(b.plannedRevenue)}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${revPct}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ EXPENSES TAB ============

function ExpensesTab() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterFarm, setFilterFarm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const { data: farms = [] } = useQuery({ queryKey: ['farms'], queryFn: financeApi.getFarms });
  const filters: Record<string, string> = {};
  if (filterFarm) filters.farmId = filterFarm;
  if (filterCategory) filters.category = filterCategory;

  const { data: expenses = [], isLoading } = useQuery({
    queryKey: ['finance-expenses', filters],
    queryFn: () => financeApi.getExpenses(filters),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => financeApi.deleteExpense(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance-expenses'] }),
  });

  const runningTotal = (Array.isArray(expenses) ? expenses : []).reduce(
    (sum: number, e: { amount: number | string }) => sum + parseFloat(String(e.amount)), 0
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FunnelIcon className="h-4 w-4 text-gray-400" />
          <select value={filterFarm} onChange={e => setFilterFarm(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Farms</option>
            {farms.map((f: { id: string; name: string }) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Categories</option>
            {Object.entries(EXPENSE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <Button variant="primary" size="sm" leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setShowAddModal(true)}>
          Add Expense
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Farm</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Category</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Description</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500">Amount (KES)</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Payment</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
              ) : !Array.isArray(expenses) || expenses.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No expenses recorded yet.</td></tr>
              ) : (
                expenses.map((e: {
                  id: string; expense_date: string; farm_name: string; category: string;
                  description: string; amount: string | number; payment_method: string;
                }) => (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{format(new Date(e.expense_date), 'MMM d, yyyy')}</td>
                    <td className="px-4 py-3">{e.farm_name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: EXPENSE_COLORS[e.category] || '#94a3b8' }} />
                        {EXPENSE_LABELS[e.category] || e.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[200px] truncate">{e.description || '-'}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatKES(parseFloat(String(e.amount)))}</td>
                    <td className="px-4 py-3 capitalize">{e.payment_method || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => deleteMutation.mutate(e.id)}
                        className="text-red-500 hover:text-red-700 text-xs font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {Array.isArray(expenses) && expenses.length > 0 && (
              <tfoot className="bg-gray-50 border-t">
                <tr>
                  <td colSpan={4} className="px-4 py-3 font-semibold text-gray-700">Total</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">{formatKES(runningTotal)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Add Expense Modal */}
      {showAddModal && (
        <AddExpenseModal farms={farms} onClose={() => setShowAddModal(false)} />
      )}
    </div>
  );
}

function AddExpenseModal({ farms, onClose }: { farms: { id: string; name: string }[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    farmId: farms[0]?.id || '', category: 'seeds', description: '',
    amount: '', quantity: '', unit: 'kg', unitPrice: '', vendor: '',
    paymentMethod: 'cash', receiptNumber: '', expenseDate: new Date().toISOString().split('T')[0],
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => financeApi.addExpense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-expenses'] });
      queryClient.invalidateQueries({ queryKey: ['finance-summary'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      ...form,
      amount: parseFloat(form.amount),
      quantity: form.quantity ? parseFloat(form.quantity) : undefined,
      unitPrice: form.unitPrice ? parseFloat(form.unitPrice) : undefined,
    });
  };

  return (
    <Modal isOpen onClose={onClose} title="Add Expense" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Farm</label>
            <select value={form.farmId} onChange={e => setForm(p => ({ ...p, farmId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" required>
              {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              {Object.entries(EXPENSE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KES)</label>
            <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" required min="0" step="0.01" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input type="date" value={form.expenseDate} onChange={e => setForm(p => ({ ...p, expenseDate: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
            <input type="number" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" min="0" step="0.01" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
            <input type="text" value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
            <input type="text" value={form.vendor} onChange={e => setForm(p => ({ ...p, vendor: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <select value={form.paymentMethod} onChange={e => setForm(p => ({ ...p, paymentMethod: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="cash">Cash</option>
              <option value="mpesa">M-Pesa</option>
              <option value="bank">Bank</option>
              <option value="credit">Credit</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input type="text" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
        </div>
        <ModalFooter>
          <Button variant="secondary" size="sm" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" size="sm" type="submit" loading={mutation.isPending}>Save Expense</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ============ INCOME TAB ============

function IncomeTab() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterFarm, setFilterFarm] = useState('');

  const { data: farms = [] } = useQuery({ queryKey: ['farms'], queryFn: financeApi.getFarms });
  const filters: Record<string, string> = {};
  if (filterFarm) filters.farmId = filterFarm;

  const { data: income = [], isLoading } = useQuery({
    queryKey: ['finance-income', filters],
    queryFn: () => financeApi.getIncome(filters),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => financeApi.deleteIncome(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance-income'] }),
  });

  const runningTotal = (Array.isArray(income) ? income : []).reduce(
    (sum: number, i: { amount: number | string }) => sum + parseFloat(String(i.amount)), 0
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FunnelIcon className="h-4 w-4 text-gray-400" />
          <select value={filterFarm} onChange={e => setFilterFarm(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Farms</option>
            {farms.map((f: { id: string; name: string }) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
        <Button variant="primary" size="sm" leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setShowAddModal(true)}>
          Record Income
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Farm</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Category</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Description</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500">Amount (KES)</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Buyer</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
              ) : !Array.isArray(income) || income.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No income recorded yet.</td></tr>
              ) : (
                income.map((i: {
                  id: string; income_date: string; farm_name: string; category: string;
                  description: string; amount: string | number; buyer: string;
                }) => (
                  <tr key={i.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{format(new Date(i.income_date), 'MMM d, yyyy')}</td>
                    <td className="px-4 py-3">{i.farm_name || '-'}</td>
                    <td className="px-4 py-3">{INCOME_LABELS[i.category] || i.category}</td>
                    <td className="px-4 py-3 max-w-[200px] truncate">{i.description || '-'}</td>
                    <td className="px-4 py-3 text-right font-medium text-green-600">{formatKES(parseFloat(String(i.amount)))}</td>
                    <td className="px-4 py-3">{i.buyer || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => deleteMutation.mutate(i.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {Array.isArray(income) && income.length > 0 && (
              <tfoot className="bg-gray-50 border-t">
                <tr>
                  <td colSpan={4} className="px-4 py-3 font-semibold text-gray-700">Total</td>
                  <td className="px-4 py-3 text-right font-bold text-green-600">{formatKES(runningTotal)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {showAddModal && <AddIncomeModal farms={farms} onClose={() => setShowAddModal(false)} />}
    </div>
  );
}

function AddIncomeModal({ farms, onClose }: { farms: { id: string; name: string }[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    farmId: farms[0]?.id || '', category: 'crop_sale', description: '',
    amount: '', quantity: '', unit: 'kg', unitPrice: '', buyer: '',
    paymentMethod: 'cash', receiptNumber: '', incomeDate: new Date().toISOString().split('T')[0],
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => financeApi.addIncome(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-income'] });
      queryClient.invalidateQueries({ queryKey: ['finance-summary'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      ...form,
      amount: parseFloat(form.amount),
      quantity: form.quantity ? parseFloat(form.quantity) : undefined,
      unitPrice: form.unitPrice ? parseFloat(form.unitPrice) : undefined,
    });
  };

  return (
    <Modal isOpen onClose={onClose} title="Record Income" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Farm</label>
            <select value={form.farmId} onChange={e => setForm(p => ({ ...p, farmId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" required>
              {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              {Object.entries(INCOME_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KES)</label>
            <input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" required min="0" step="0.01" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input type="date" value={form.incomeDate} onChange={e => setForm(p => ({ ...p, incomeDate: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
            <input type="number" value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" min="0" step="0.01" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buyer</label>
            <input type="text" value={form.buyer} onChange={e => setForm(p => ({ ...p, buyer: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <select value={form.paymentMethod} onChange={e => setForm(p => ({ ...p, paymentMethod: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="cash">Cash</option>
              <option value="mpesa">M-Pesa</option>
              <option value="bank">Bank</option>
              <option value="check">Check</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input type="text" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
        </div>
        <ModalFooter>
          <Button variant="secondary" size="sm" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" size="sm" type="submit" loading={mutation.isPending}>Save Income</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ============ BUDGETS TAB ============

function BudgetsTab() {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState<string | null>(null);

  const { data: farms = [] } = useQuery({ queryKey: ['farms'], queryFn: financeApi.getFarms });
  const { data: budgets = [], isLoading } = useQuery({
    queryKey: ['finance-budgets'],
    queryFn: () => financeApi.getBudgets(),
  });

  const { data: budgetDetail } = useQuery({
    queryKey: ['finance-budget', selectedBudget],
    queryFn: () => financeApi.getBudget(selectedBudget!),
    enabled: !!selectedBudget,
  });

  const closeMutation = useMutation({
    mutationFn: (id: string) => financeApi.closeBudget(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance-budgets'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Season Budgets</h3>
        <Button variant="primary" size="sm" leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setShowCreate(true)}>
          New Budget
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kenagri-600" />
        </div>
      ) : !Array.isArray(budgets) || budgets.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-400">
          No budgets created yet. Create a budget to track seasonal expenses vs. revenue.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {budgets.map((b: {
            id: string; season: string; farm_name: string; crop_type: string; status: string;
            planned_revenue: string; actual_revenue: string; planned_expenses: string; actual_expenses: string;
          }) => {
            const plannedExp = parseFloat(b.planned_expenses);
            const actualExp = parseFloat(b.actual_expenses);
            const plannedRev = parseFloat(b.planned_revenue);
            const actualRev = parseFloat(b.actual_revenue);
            const expPct = plannedExp > 0 ? Math.min((actualExp / plannedExp) * 100, 100) : 0;

            return (
              <div
                key={b.id}
                className="bg-white rounded-xl shadow-sm border p-5 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedBudget(b.id === selectedBudget ? null : b.id)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900">{b.season}</p>
                    <p className="text-sm text-gray-500">{b.farm_name || 'Unknown Farm'} {b.crop_type ? `- ${b.crop_type}` : ''}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    b.status === 'active' ? 'bg-green-50 text-green-700' :
                    b.status === 'closed' ? 'bg-gray-100 text-gray-600' :
                    'bg-amber-50 text-amber-700'
                  }`}>
                    {b.status}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Expenses</span>
                    <span>{formatKES(actualExp)} / {formatKES(plannedExp)}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${expPct > 90 ? 'bg-red-500' : expPct > 70 ? 'bg-amber-500' : 'bg-kenagri-500'}`} style={{ width: `${expPct}%` }} />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Revenue</span>
                    <span className="text-green-600">{formatKES(actualRev)} / {formatKES(plannedRev)}</span>
                  </div>
                </div>
                {b.status === 'active' && (
                  <div className="mt-3 pt-3 border-t">
                    <button
                      onClick={(e) => { e.stopPropagation(); closeMutation.mutate(b.id); }}
                      className="text-sm text-kenagri-600 hover:text-kenagri-700 font-medium"
                    >
                      Close Budget
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Budget Detail */}
      {selectedBudget && budgetDetail && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Budget Detail: {budgetDetail.season}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="text-sm font-medium text-gray-500 mb-2">Expense Breakdown</h5>
              {budgetDetail.expenseBreakdown?.length > 0 ? (
                <div className="space-y-2">
                  {budgetDetail.expenseBreakdown.map((e: { category: string; total: string; count: string }) => (
                    <div key={e.category} className="flex justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: EXPENSE_COLORS[e.category] || '#94a3b8' }} />
                        {EXPENSE_LABELS[e.category] || e.category} ({e.count})
                      </span>
                      <span className="font-medium">{formatKES(parseFloat(e.total))}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-gray-400">No expenses linked to this budget</p>}
            </div>
            <div>
              <h5 className="text-sm font-medium text-gray-500 mb-2">Income Breakdown</h5>
              {budgetDetail.incomeBreakdown?.length > 0 ? (
                <div className="space-y-2">
                  {budgetDetail.incomeBreakdown.map((i: { category: string; total: string; count: string }) => (
                    <div key={i.category} className="flex justify-between text-sm">
                      <span>{INCOME_LABELS[i.category] || i.category} ({i.count})</span>
                      <span className="font-medium text-green-600">{formatKES(parseFloat(i.total))}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-gray-400">No income linked to this budget</p>}
            </div>
          </div>
        </div>
      )}

      {showCreate && <CreateBudgetModal farms={farms} onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function CreateBudgetModal({ farms, onClose }: { farms: { id: string; name: string }[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    farmId: farms[0]?.id || '', season: '', cropType: '',
    plannedRevenue: '', plannedExpenses: '', notes: '',
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => financeApi.createBudget(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-budgets'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      ...form,
      plannedRevenue: form.plannedRevenue ? parseFloat(form.plannedRevenue) : 0,
      plannedExpenses: form.plannedExpenses ? parseFloat(form.plannedExpenses) : 0,
    });
  };

  return (
    <Modal isOpen onClose={onClose} title="Create Budget" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Farm</label>
            <select value={form.farmId} onChange={e => setForm(p => ({ ...p, farmId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" required>
              {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Season</label>
            <input type="text" value={form.season} onChange={e => setForm(p => ({ ...p, season: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" required placeholder="e.g. 2025-long-rains" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Crop Type</label>
            <input type="text" value={form.cropType} onChange={e => setForm(p => ({ ...p, cropType: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. Maize" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Planned Revenue (KES)</label>
            <input type="number" value={form.plannedRevenue} onChange={e => setForm(p => ({ ...p, plannedRevenue: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" min="0" step="1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Planned Expenses (KES)</label>
            <input type="number" value={form.plannedExpenses} onChange={e => setForm(p => ({ ...p, plannedExpenses: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" min="0" step="1" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} />
        </div>
        <ModalFooter>
          <Button variant="secondary" size="sm" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" size="sm" type="submit" loading={mutation.isPending}>Create Budget</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ============ LOANS TAB ============

function LoansTab() {
  const queryClient = useQueryClient();
  const [showAddLoan, setShowAddLoan] = useState(false);
  const [payingLoan, setPayingLoan] = useState<string | null>(null);
  const [payAmount, setPayAmount] = useState('');

  const { data: farms = [] } = useQuery({ queryKey: ['farms'], queryFn: financeApi.getFarms });
  const { data: loans = [], isLoading } = useQuery({
    queryKey: ['finance-loans'],
    queryFn: () => financeApi.getLoans(),
  });
  const { data: loanSummary } = useQuery({
    queryKey: ['finance-loan-summary'],
    queryFn: financeApi.getLoanSummary,
  });

  const paymentMutation = useMutation({
    mutationFn: ({ id, amount }: { id: string; amount: number }) => financeApi.recordPayment(id, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-loans'] });
      queryClient.invalidateQueries({ queryKey: ['finance-loan-summary'] });
      setPayingLoan(null);
      setPayAmount('');
    },
  });

  return (
    <div className="space-y-4">
      {/* Loan Summary */}
      {loanSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-sm text-gray-500">Total Debt</p>
            <p className="text-xl font-bold text-red-600">{formatKES(loanSummary.totalDebt)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-sm text-gray-500">Monthly Obligations</p>
            <p className="text-xl font-bold text-amber-600">{formatKES(loanSummary.monthlyObligations)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-sm text-gray-500">Active Loans</p>
            <p className="text-xl font-bold">{loanSummary.activeLoans}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <p className="text-sm text-gray-500">Total Paid</p>
            <p className="text-xl font-bold text-green-600">{formatKES(loanSummary.totalPaid)}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Loans</h3>
        <Button variant="primary" size="sm" leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setShowAddLoan(true)}>
          Add Loan
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kenagri-600" />
        </div>
      ) : !Array.isArray(loans) || loans.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-400">
          No loans recorded yet.
        </div>
      ) : (
        <div className="space-y-3">
          {loans.map((l: {
            id: string; lender: string; loan_type: string; principal: string;
            interest_rate: string; remaining_balance: string; monthly_payment: string;
            total_paid: string; status: string; disbursement_date: string; maturity_date: string; farm_name: string;
          }) => {
            const principal = parseFloat(l.principal);
            const remaining = parseFloat(l.remaining_balance);
            const paidPct = principal > 0 ? Math.min(((principal - remaining) / principal) * 100, 100) : 0;

            return (
              <div key={l.id} className="bg-white rounded-xl shadow-sm border p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-900">{l.lender}</p>
                    <p className="text-sm text-gray-500">
                      {LOAN_TYPE_LABELS[l.loan_type] || l.loan_type}
                      {l.farm_name ? ` - ${l.farm_name}` : ''}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    l.status === 'active' ? 'bg-blue-50 text-blue-700' :
                    l.status === 'completed' ? 'bg-green-50 text-green-700' :
                    l.status === 'defaulted' ? 'bg-red-50 text-red-700' :
                    'bg-amber-50 text-amber-700'
                  }`}>
                    {l.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3 text-sm">
                  <div>
                    <span className="text-gray-500">Principal</span>
                    <p className="font-medium">{formatKES(principal)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Interest Rate</span>
                    <p className="font-medium">{l.interest_rate ? `${l.interest_rate}%` : 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Remaining</span>
                    <p className="font-medium text-red-600">{formatKES(remaining)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Monthly Payment</span>
                    <p className="font-medium">{l.monthly_payment ? formatKES(parseFloat(l.monthly_payment)) : 'N/A'}</p>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Repayment Progress</span>
                    <span>{paidPct.toFixed(0)}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-kenagri-500 rounded-full" style={{ width: `${paidPct}%` }} />
                  </div>
                </div>

                {l.status === 'active' && (
                  <div className="flex items-center gap-2 pt-2 border-t">
                    {payingLoan === l.id ? (
                      <>
                        <input
                          type="number"
                          value={payAmount}
                          onChange={e => setPayAmount(e.target.value)}
                          placeholder="Amount (KES)"
                          className="border rounded-lg px-3 py-1.5 text-sm w-40"
                          min="0"
                          step="1"
                        />
                        <Button
                          variant="primary"
                          size="xs"
                          loading={paymentMutation.isPending}
                          onClick={() => paymentMutation.mutate({ id: l.id, amount: parseFloat(payAmount) })}
                          disabled={!payAmount || parseFloat(payAmount) <= 0}
                        >
                          Confirm
                        </Button>
                        <Button variant="ghost" size="xs" onClick={() => { setPayingLoan(null); setPayAmount(''); }}>
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button variant="outline" size="xs" onClick={() => setPayingLoan(l.id)}>
                        Record Payment
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAddLoan && <AddLoanModal farms={farms} onClose={() => setShowAddLoan(false)} />}
    </div>
  );
}

function AddLoanModal({ farms, onClose }: { farms: { id: string; name: string }[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    farmId: '', lender: '', loanType: 'input_financing',
    principal: '', interestRate: '', termMonths: '', monthlyPayment: '',
    disbursementDate: new Date().toISOString().split('T')[0], maturityDate: '', notes: '',
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => financeApi.addLoan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance-loans'] });
      queryClient.invalidateQueries({ queryKey: ['finance-loan-summary'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      farmId: form.farmId || undefined,
      lender: form.lender,
      loanType: form.loanType,
      principal: parseFloat(form.principal),
      interestRate: form.interestRate ? parseFloat(form.interestRate) : undefined,
      termMonths: form.termMonths ? parseInt(form.termMonths) : undefined,
      monthlyPayment: form.monthlyPayment ? parseFloat(form.monthlyPayment) : undefined,
      disbursementDate: form.disbursementDate || undefined,
      maturityDate: form.maturityDate || undefined,
      notes: form.notes || undefined,
    });
  };

  return (
    <Modal isOpen onClose={onClose} title="Add Loan" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lender</label>
            <input type="text" value={form.lender} onChange={e => setForm(p => ({ ...p, lender: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" required placeholder="e.g. KCB Bank" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type</label>
            <select value={form.loanType} onChange={e => setForm(p => ({ ...p, loanType: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              {Object.entries(LOAN_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Farm (optional)</label>
            <select value={form.farmId} onChange={e => setForm(p => ({ ...p, farmId: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">-- No specific farm --</option>
              {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Principal (KES)</label>
            <input type="number" value={form.principal} onChange={e => setForm(p => ({ ...p, principal: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" required min="0" step="1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Interest Rate (%)</label>
            <input type="number" value={form.interestRate} onChange={e => setForm(p => ({ ...p, interestRate: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" min="0" step="0.1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Term (months)</label>
            <input type="number" value={form.termMonths} onChange={e => setForm(p => ({ ...p, termMonths: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" min="1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Payment (KES)</label>
            <input type="number" value={form.monthlyPayment} onChange={e => setForm(p => ({ ...p, monthlyPayment: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" min="0" step="1" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Disbursement Date</label>
            <input type="date" value={form.disbursementDate} onChange={e => setForm(p => ({ ...p, disbursementDate: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Maturity Date</label>
            <input type="date" value={form.maturityDate} onChange={e => setForm(p => ({ ...p, maturityDate: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} />
        </div>
        <ModalFooter>
          <Button variant="secondary" size="sm" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" size="sm" type="submit" loading={mutation.isPending}>Add Loan</Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ============ REPORTS TAB ============

function ReportsTab() {
  const [reportFarm, setReportFarm] = useState('');
  const [reportSeason, setReportSeason] = useState('');

  const { data: farms = [] } = useQuery({ queryKey: ['farms'], queryFn: financeApi.getFarms });

  const { data: pnl } = useQuery({
    queryKey: ['finance-pnl', reportFarm],
    queryFn: () => financeApi.getProfitLoss(reportFarm || undefined),
  });

  const { data: roi } = useQuery({
    queryKey: ['finance-roi', reportFarm, reportSeason],
    queryFn: () => financeApi.getROI(reportFarm || undefined, reportSeason || undefined),
  });

  const { data: breakEven } = useQuery({
    queryKey: ['finance-breakeven', reportFarm, reportSeason],
    queryFn: () => financeApi.getBreakEven(reportFarm, reportSeason),
    enabled: !!reportFarm && !!reportSeason,
  });

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex items-center gap-3">
        <select value={reportFarm} onChange={e => setReportFarm(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="">All Farms</option>
          {farms.map((f: { id: string; name: string }) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <input
          type="text"
          value={reportSeason}
          onChange={e => setReportSeason(e.target.value)}
          placeholder="Season (e.g. 2025-long-rains)"
          className="border rounded-lg px-3 py-2 text-sm w-60"
        />
        <Button variant="secondary" size="sm" leftIcon={<DocumentArrowDownIcon className="h-4 w-4" />}>
          Export PDF
        </Button>
      </div>

      {/* P&L Statement */}
      {pnl && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Profit & Loss Statement</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-3 uppercase">Income</h4>
              {pnl.incomeByCategory?.length > 0 ? (
                <div className="space-y-2">
                  {pnl.incomeByCategory.map((i: { category: string; total: string }) => (
                    <div key={i.category} className="flex justify-between text-sm">
                      <span>{INCOME_LABELS[i.category] || i.category}</span>
                      <span className="font-medium text-green-600">{formatKES(parseFloat(i.total))}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-bold border-t pt-2 mt-2">
                    <span>Total Income</span>
                    <span className="text-green-600">{formatKES(pnl.totalIncome)}</span>
                  </div>
                </div>
              ) : <p className="text-sm text-gray-400">No income data</p>}
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-3 uppercase">Expenses</h4>
              {pnl.expenseByCategory?.length > 0 ? (
                <div className="space-y-2">
                  {pnl.expenseByCategory.map((e: { category: string; total: string }) => (
                    <div key={e.category} className="flex justify-between text-sm">
                      <span>{EXPENSE_LABELS[e.category] || e.category}</span>
                      <span className="font-medium text-red-600">{formatKES(parseFloat(e.total))}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-bold border-t pt-2 mt-2">
                    <span>Total Expenses</span>
                    <span className="text-red-600">{formatKES(pnl.totalExpenses)}</span>
                  </div>
                </div>
              ) : <p className="text-sm text-gray-400">No expense data</p>}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t-2 flex justify-between items-center">
            <span className="text-lg font-bold">Net Profit</span>
            <span className={`text-2xl font-bold ${pnl.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatKES(pnl.netProfit)}
            </span>
          </div>
          {pnl.profitMargin !== undefined && (
            <p className="text-sm text-gray-500 mt-1 text-right">
              Profit Margin: {pnl.profitMargin.toFixed(1)}%
            </p>
          )}
        </div>
      )}

      {/* ROI */}
      {roi && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Return on Investment</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Total Investment</p>
              <p className="text-xl font-bold">{formatKES(roi.totalInvestment)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Returns</p>
              <p className="text-xl font-bold text-green-600">{formatKES(roi.totalReturns)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Net Return</p>
              <p className={`text-xl font-bold ${roi.netReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatKES(roi.netReturn)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">ROI</p>
              <p className={`text-xl font-bold ${roi.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>{roi.roi.toFixed(1)}%</p>
            </div>
          </div>
        </div>
      )}

      {/* Break-Even */}
      {reportFarm && reportSeason && breakEven && (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Break-Even Analysis</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500">Fixed Costs</p>
              <p className="text-lg font-bold">{formatKES(breakEven.fixedCosts)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Variable Costs</p>
              <p className="text-lg font-bold">{formatKES(breakEven.variableCosts)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Costs</p>
              <p className="text-lg font-bold">{formatKES(breakEven.totalCosts)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Revenue</p>
              <p className="text-lg font-bold text-green-600">{formatKES(breakEven.totalRevenue)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Break-Even Units</p>
              <p className="text-lg font-bold">{breakEven.breakEvenUnits.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Break-Even Revenue</p>
              <p className="text-lg font-bold">{formatKES(breakEven.breakEvenRevenue)}</p>
            </div>
          </div>

          {/* Break-even visualization */}
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { name: 'Fixed Costs', value: breakEven.fixedCosts, fill: '#94a3b8' },
                { name: 'Variable Costs', value: breakEven.variableCosts, fill: '#f59e0b' },
                { name: 'Break-Even', value: breakEven.breakEvenRevenue, fill: '#3b82f6' },
                { name: 'Actual Revenue', value: breakEven.totalRevenue, fill: '#22c55e' },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value: number) => formatKES(value)} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {[
                    { fill: '#94a3b8' },
                    { fill: '#f59e0b' },
                    { fill: '#3b82f6' },
                    { fill: '#22c55e' },
                  ].map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 pt-4 border-t text-center">
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
              breakEven.isProfitable ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {breakEven.isProfitable ? 'Profitable - Above break-even point' : 'Below break-even point'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
